<script>
import { mapActions, mapState } from 'vuex';
import dateMixin from '../../mixins/dateMixin';
import currencies from '../../mixins/currencies';
import numberMixin from '../../mixins/numberMixin';
import link from '../../mixins/link';
import ReminderListsBookingMessages from '../../components/Reminders/ReminderListsBookingMessages';
import ReminderListsTableInfo from '../../components/Reminders/ReminderListsTableInfo';
import ReminderListsTableFilter from '../../components/Reminders/ReminderListsTableFilter';

export default {
  name: 'ReminderListsArchived',
  mixins: [
    dateMixin,
    currencies,
    numberMixin,
    link,
  ],
  components: {
    ReminderListsBookingMessages,
    ReminderListsTableInfo,
    ReminderListsTableFilter,
  },
  data() {
    return {
      headers: [
        { text: '', value: '', sortable: false, width: '40px', class: 'pa-0 pr-1' },
        { text: 'Booking ID', value: 'bookingId' },
        { text: 'Customer', value: 'customer' },
        { text: 'Supplier', value: 'bookingStay.supplier' },
        { text: 'Supplier Cancellation', value: 'bookingStay.latestDateToSupplierCancel' },
        { text: 'Domain', value: 'domainName' },
        { text: 'Booking Date', value: 'bookingStay.createdAt' },
        { text: 'Arrival Date', value: 'bookingStay.arrivalDate' },
        { text: 'Due Date', value: 'dueDate' },
        { text: 'Amount', value: 'amount' },
      ],
      pagination: {
        rowsPerPage: 25,
        sortBy: 'bookingStay.arrivalDate',
        descending: true,
      },
      showMessages: false,
    };
  },
  computed: {
    ...mapState({
      list: state => state.reminders.lists.list,
      filters: state => state.reminders.lists.filters,
    }),
    computedList() {
      if (!this.list.items.length) {
        return [];
      }
      return this.list.items.filter((booking) => {
        if (this.filters.chosenDomains.length && !this.filters.chosenDomains.includes(booking.domainId)) {
          return false;
        }
        if (this.filters.showMessages === 'only' && !booking.bookingStay.hasPaymentRemark) {
          return false;
        }
        if (this.filters.showMessages === 'none' && booking.bookingStay.hasPaymentRemark) {
          return false;
        }
        if (this.filters.showCancelled === 'only' && !booking.bookingStay.cancelledDate) {
          return false;
        }
        if (this.filters.showCancelled === 'none' && booking.bookingStay.cancelledDate) {
          return false;
        }
        if (this.filters.search) {
          const searchIn = `${booking.customer} ${booking.bookingStay.supplier}`.toLowerCase();
          return searchIn.indexOf(this.filters.search.trim().toLowerCase()) !== -1;
        }
        return true;
      });
    },
  },
  methods: {
    ...mapActions({
      setBreadcrumbs: 'nav/setBreadcrumbs',
      getList: 'reminders/getList',
      getMessages: 'reminders/getPaymentMessages',
    }),
    showPaymentMessages(booking) {
      this.getMessages(booking);
      this.showMessages = true;
    },
    getPaymentStatus(bookingStay) {
      if (bookingStay.paidAmount === 0) {
        return 'error';
      }
      if (bookingStay.paidAmount < bookingStay.totalAmount) {
        return 'orange';
      }
      if (bookingStay.paidAmount === bookingStay.totalAmount) {
        return 'green';
      }
      return 'blue';
    },
    goToBooking(booking) {
      const bookingLink = this.getLinkToTwentyPage(
        `booking/bookings/${booking.bookingId}/info?stay=${booking.bookingStay.id}`,
      );
      window.open(bookingLink, '_blank');
    },
  },
  created() {
    this.setBreadcrumbs({
      breadcrumbs: [
        { text: 'Reminders', to: '', disabled: true },
        { text: 'Lists', to: '.' },
        { text: 'Last Reminder', to: '.' },
        { text: 'Archived', to: this.$route.path },
      ],
      title: 'Lists',
    });
    this.getList('archived');
  },
};
</script>
<template>
  <v-layout wrap>
    <v-flex xs12 mb-4>
      <h1>Archived Bookings</h1>
      <span class="subheading">
        This list contains booking where the reminder system has been archived.
      </span>
    </v-flex>
    <reminder-lists-table-filter @refresh="getList('archived')"/>
    <v-flex xs12 reminder-lists__archived-table>
      <reminder-lists-table-info/>
      <v-data-table
        :headers="headers"
        :items="computedList"
        :loading="list.loading"
        :pagination.sync="pagination"
        must-sort
      >
        <template v-slot:items="props">
          <tr>
            <td class="pa-0 pr-2 reminder-lists__archived-status">
              <v-tooltip right>
                <template v-slot:activator="{ on }">
                  <div
                    v-on="on"
                    :class="getPaymentStatus(props.item.bookingStay)"
                    class="reminder-lists__archived-status-box"
                  />
                </template>
                <div>
                  <span>Paid: </span>
                  <span class="caption">{{ props.item.bookingStay.paidAmount | formatNumber }}</span>
                  <span class="py-2">-</span>
                  <span>Amount missing: </span>
                  <span class="caption">
                    {{ (props.item.bookingStay.totalAmount - props.item.bookingStay.paidAmount) | formatNumber }}
                  </span>
                </div>
              </v-tooltip>
              <v-tooltip :disabled="!props.item.bookingStay.cancelledDate" right>
                <template v-slot:activator="{ on }">
                  <div
                    v-on="on"
                    :class="{ 'error': !!props.item.bookingStay.cancelledDate }"
                    class="reminder-lists__archived-status-box green lighten-2"
                  />
                </template>
                <span>Cancelled: {{ formatDate(props.item.bookingStay.cancelledDate) }}</span>
              </v-tooltip>
              <v-tooltip v-if="props.item.bookingStay.hasPaymentRemark" right>
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-on="on"
                    class="teal--text text--lighten-3 reminder-lists__archived-status-remark"
                    @click="showPaymentMessages(props.item)"
                  >
                    info
                  </v-icon>
                </template>
                <span class="body-2">Click to see remarks</span>
              </v-tooltip>
            </td>
            <td>
              <span class="cursor-pointer external-link blue--text" @click="goToBooking(props.item)">
                {{ props.item.bookingId }}
              </span>
            </td>
            <td>{{ props.item.customer }}</td>
            <td>{{ props.item.bookingStay.supplier }}</td>
            <td>
              {{ formatDateIgnoreTimezone(props.item.bookingStay.latestDateToSupplierCancel, 'DD-MM-YYYY') }}
              {{ props.item.bookingStay.supplierCancelPercent }}%
              ({{ props.item.bookingStay.daysBeforeNextSupplierCancel }})
            </td>
            <td>{{ props.item.domainName }}</td>
            <td>{{ formatDate(props.item.bookingStay.createdAt) }}</td>
            <td>
              {{ formatDate(props.item.bookingStay.arrivalDate, 'DD-MM-YYYY') }}
              ({{ props.item.bookingStay.daysBeforeArrival }})
            </td>
            <td>{{ formatDateIgnoreTimezone(props.item.dueDate, 'DD-MM-YYYY') }}</td>
            <td>{{ props.item.amount | formatNumber }} {{ formatCurrency(props.item.currency) }}</td>
          </tr>
        </template>
      </v-data-table>
      <reminder-lists-booking-messages :visible="showMessages" @hide="showMessages = false"/>
    </v-flex>
  </v-layout>
</template>

<style lang="scss">
.reminder-lists__archived {
  &-table {
    position: relative;
  }

  &-status {
    font-size: 0 !important;
    position: relative;

    &-box {
      height: 100%;
      width: 12px;
      display: inline-block;
    }

    &-remark {
      position: absolute;
      top: 50%;
      right: -8px;
      transform: translateY(-50%);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/reminders/ReminderListsArchived.vue